// filepath: apps/web/src/features/tasks/useCompleteTask.ts
//
// TanStack Query mutation hook for completing a task (FR-HT-3 / FR-UI-TASK-1).
//
// AC-4: submits the pinned-version form data with the task's row_version.
// AC-5: on HTTP 409 → routes through useConflictResolver.show() so the
// ConflictResolution modal opens with server's currentRecord and user's
// pending form data.
//
// INV-7: the row_version from the task payload is always sent. The server
// enforces optimistic concurrency and returns 409 on mismatch.
// INV-2: the merged reapply payload drops server-stripped fields —
// delegated to useConflictResolver.mergeReapplyPayload (already audited).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError, HttpConflictError } from "@/definitions/types";
import { apiFetch } from "@/api";
import { taskKeys } from "./useTasks";

/** POST /api/tasks/{id}/complete */
interface CompleteTaskVariables {
  taskId: string;
  /** The row_version from the HumanTask payload (captured at claim time). */
  rowVersion: number;
  outcome?: string;
  formData: Record<string, unknown>;
}

interface CompleteSuccess {
  id: string;
  status: string;
}

/**
 * Options for useCompleteTask.
 * onConflict: called when server returns 409 — the caller opens the ConflictResolution modal.
 * onSuccess: called on successful completion — the caller navigates to the inbox.
 */
export interface UseCompleteTaskOptions {
  onConflict?: (
    err: HttpConflictError,
    userRecord: Record<string, unknown>,
  ) => void;
  onSuccess?: (result: CompleteSuccess) => void;
}

export function useCompleteTask(
  opts: UseCompleteTaskOptions = {},
  apiBase = "/api",
): {
  mutate: (variables: CompleteTaskVariables) => void;
  mutateAsync: (
    variables: CompleteTaskVariables,
  ) => Promise<CompleteSuccess | undefined>;
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
} {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    CompleteSuccess,
    ApiError,
    CompleteTaskVariables
  >({
    mutationFn: async ({ taskId, rowVersion, outcome, formData }) => {
      const url = `${apiBase}/tasks/${encodeURIComponent(taskId)}/complete`;
      const body = {
        task_id: taskId,
        row_version: rowVersion,
        outcome,
        form_data: formData,
      };

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // task_id + row_version are forwarded via the request
        // body (server-side INV-7 + INV-2 contract); the
        // optimistic-concurrency 409 path is preserved verbatim.
        response = await apiFetch(url, {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(body),
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (response.status === 409) {
        // FR-ENT-8 / FR-UI-CORE-6: 409 carries the server's current record
        // and fresh row_version. Throw HttpConflictError so the caller can
        // route to useConflictResolver.show().
        let currentRowVersion: number | null = null;
        let currentRecord: Record<string, unknown> | null = null;
        try {
          const raw = (await response.json()) as {
            current_row_version?: number;
            current_record?: Record<string, unknown>;
          };
          currentRowVersion = raw.current_row_version ?? null;
          currentRecord = raw.current_record ?? null;
        } catch {
          // Graceful degradation — old server may return body-less 409.
        }
        throw new HttpConflictError(
          "write conflict",
          409,
          "row_version_conflict",
          currentRowVersion,
          currentRecord,
        );
      }

      if (!response.ok) {
        const code = `http_${response.status}`;
        throw new ApiError(
          response.statusText || "complete failed",
          response.status,
          code,
        );
      }

      return response.json() as Promise<CompleteSuccess>;
    },

    onSuccess: (result) => {
      // Invalidate the inbox so the completed task disappears from "My Tasks".
      void queryClient.invalidateQueries({
        queryKey: taskKeys.inbox(tenantSlug, "mine"),
      });
      void queryClient.invalidateQueries({
        queryKey: taskKeys.inbox(tenantSlug, "unclaimed"),
      });
      opts.onSuccess?.(result);
    },

    onError: (err) => {
      if (err instanceof HttpConflictError) {
        // Capture the user's pending form data at the time of the 409.
        // The ConflictResolution modal needs this for the diff view.
        const vars = mutation.variables;
        const userRecord: Record<string, unknown> = vars
          ? {
              ...vars.formData,
              task_id: vars.taskId,
              row_version: vars.rowVersion,
            }
          : {};
        opts.onConflict?.(err, userRecord);
        return;
      }
      // Non-conflict errors propagate to the caller (e.g. 400, 404, 5xx).
      // The TaskComplete component displays a toast / error state.
    },

    retry: (count, err) => {
      // Never retry 409 — user must pick a resolution action.
      if (err instanceof HttpConflictError) return false;
      return count < 2;
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}
